/**
 * What each role sees of other people's work, on the screens that show it.
 *
 * Split out of the pages for the same reason the nav model was: these are
 * pure functions of a role, and asserting them should not require mounting a
 * page that also builds queries, forms and a table. The pages call these and
 * render what they return, so there is no second rule for a test to agree
 * with while a screen is wrong.
 *
 * Two questions, deliberately separate — they have different answers for the
 * same person. An owner monitors other people's calls but does not log any;
 * a recruiter logs calls and sees nobody's but their own.
 */
import { canSeeAllCompanies, isSupervisor } from '../store/auth';
import type { User } from '../types';

/**
 * Whether the Log a call form is offered.
 *
 * Everyone except the owner. Logging a call is the work of whoever made it,
 * and the owner is on this screen to see what the team did rather than to add
 * to it — a form they will never use is a form in the way of the report.
 *
 * A visibility rule, not a permission one. POST /calls is unchanged and still
 * accepts any staff member including the owner, exactly as it did; nothing
 * about who *may* log a call has moved. If that ever needs to be a real
 * restriction it belongs on the endpoint, and this would follow it rather
 * than stand in for it.
 */
export function canLogCalls(user: User | null): boolean {
  return !canSeeAllCompanies(user);
}

/**
 * Whether the screen shows whose work each row is, and lets it be filtered.
 *
 * isSupervisor, which is owner, company admin and manager — the same
 * predicate the Joining Tracker already uses to decide who gets its recruiter
 * filter. Matching it rather than inventing a narrower one is deliberate: two
 * screens over the same people's work that disagree about who may see the
 * names would be a rule nobody could state.
 *
 * It follows what these roles can already read rather than widening anything.
 * subtree_user_ids gives a manager their reporting line and an owner or admin
 * the whole company, so all three are already looking at rows belonging to
 * several people with no way to tell them apart. A recruiter is restricted to
 * their own rows, which is why they get neither: the column would repeat one
 * name and the filter would offer one option.
 */
export function seesOthersWork(user: User | null): boolean {
  return isSupervisor(user);
}
